interface HistoryPanelProps {
  pastCount: number;
  futureCount: number;
  onJumpBack: (steps: number) => void;
  onJumpForward: (steps: number) => void;
  onClose: () => void;
}

/**
 * Right-rail history panel. Each row represents one "change" (one undo step).
 * Phase 4 v1 — labels are generic ("Modification de la fiche"). Phase 5 will
 * tag each entry with the actual action (e.g. "Bullet edited", "Section moved")
 * by comparing adjacent pastStates.
 *
 * Reads counts from the editor store via props. The actual past/future
 * arrays aren't shown verbatim — only the count + click-to-jump.
 */
export function HistoryPanel({
  pastCount,
  futureCount,
  onJumpBack,
  onJumpForward,
  onClose,
}: HistoryPanelProps) {
  return (
    <aside
      className="fixed top-0 right-0 bottom-0 w-[320px] bg-paper border-l-2 border-ink shadow-ink z-30 flex flex-col"
      aria-label="Historique des modifications"
      data-testid="history-panel"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-ink/30">
        <h3 className="font-gloock text-[18px] text-ink">Historique</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer l'historique"
          className="text-ink hover:bg-ink/10 rounded-full w-7 h-7 grid place-items-center"
        >
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {pastCount === 0 && futureCount === 0 ? (
          <p className="font-caveat text-ink-soft text-[16px] text-center mt-4">
            Aucune modification pour l'instant.
          </p>
        ) : null}

        {futureCount > 0 ? (
          <section className="flex flex-col gap-1">
            <h4 className="font-caveat text-ink-soft text-[14px] uppercase tracking-wide">
              À refaire ({futureCount})
            </h4>
            {Array.from({ length: futureCount }).map((_, i) => (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: list order matches zundo's stack
                key={`fwd-${i}`}
                type="button"
                onClick={() => onJumpForward(i + 1)}
                className="text-left px-3 py-2 border-2 border-ink/30 rounded-control bg-white hover:bg-accent-blue/10 font-hand text-[14px] flex items-center gap-2"
              >
                <span aria-hidden>↷</span>
                <span>Étape {i + 1} à refaire</span>
              </button>
            ))}
          </section>
        ) : null}

        {pastCount > 0 ? (
          <section className="flex flex-col gap-1 mt-2">
            <h4 className="font-caveat text-ink-soft text-[14px] uppercase tracking-wide">
              Modifications ({pastCount})
            </h4>
            {Array.from({ length: pastCount }).map((_, i) => {
              const stepsBack = i + 1;
              return (
                <button
                  // biome-ignore lint/suspicious/noArrayIndexKey: list order matches zundo's stack
                  key={`back-${i}`}
                  type="button"
                  onClick={() => onJumpBack(stepsBack)}
                  className="text-left px-3 py-2 border-2 border-ink/30 rounded-control bg-white hover:bg-accent-yellow/30 font-hand text-[14px] flex items-center gap-2"
                >
                  <span aria-hidden>👤</span>
                  <span>
                    Modification #{pastCount - i} —{' '}
                    <span className="text-ink-soft text-[12px]">
                      revenir en arrière ({stepsBack} étape{stepsBack > 1 ? 's' : ''})
                    </span>
                  </span>
                </button>
              );
            })}
          </section>
        ) : null}
      </div>

      <footer className="px-4 py-2 border-t border-ink/30 font-caveat text-ink-soft text-[14px]">
        Cmd/Ctrl + Z · Cmd/Ctrl + Shift + Z
      </footer>
    </aside>
  );
}
